import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Stars, Moon } from "lucide-react";
import { CosmicBackground } from "../components/CosmicBackground";
import { Button } from "../components/ui/button";

export default function Landing() {
  const navigate = useNavigate();

  const handleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <CosmicBackground>
      {/* Hero */}
      <div
        className="absolute inset-0 -z-10 opacity-40"
        style={{
          backgroundImage:
            "url('https://images.pexels.com/photos/23522893/pexels-photo-23522893.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-[#05070A]/30 via-[#05070A]/60 to-[#05070A]" />

      <header className="max-w-7xl mx-auto px-6 md:px-12 py-6 flex items-center justify-between">
        <div className="flex items-center gap-2" data-testid="landing-logo">
          <Stars size={20} strokeWidth={1.5} className="text-[#D4AF37]" />
          <span className="font-serif-mystic text-xl tracking-wide">AuraQuest</span>
        </div>
        <Button
          onClick={handleSignIn}
          data-testid="landing-signin-top-btn"
          variant="ghost"
          className="text-[#D4AF37] hover:bg-[#D4AF37]/10"
        >
          Sign in
        </Button>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-24 grid lg:grid-cols-2 gap-12 items-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="flex items-center gap-2 text-[#D4AF37] mb-4">
            <Sparkles size={14} strokeWidth={1.5} />
            <span className="text-xs uppercase tracking-[0.3em] font-semibold">
              Human Design · Astrology · Sacred Self
            </span>
          </div>
          <h1 className="font-serif-mystic text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] mb-6">
            Your cosmic <span className="gold-shimmer">blueprint</span>,
            <br />
            decoded with reverence.
          </h1>
          <p className="text-[#94A3B8] text-base sm:text-lg max-w-xl mb-8 leading-relaxed">
            AuraQuest is a daily ritual built from your exact moment of arrival —
            Human Design bodygraph, natal chart and personalised quests
            calibrated to your aura type.
          </p>
          <div className="flex flex-wrap items-center gap-4">
            <Button
              data-testid="landing-signin-btn"
              onClick={handleSignIn}
              size="lg"
              className="bg-[#D4AF37] text-[#05070A] hover:bg-[#F3E5AB] transition-colors px-8 py-6 text-base font-medium tracking-wide shadow-[0_0_30px_rgba(212,175,55,0.4)]"
            >
              <Sparkles size={16} strokeWidth={2} className="mr-2" />
              Begin with Google
            </Button>
            <a
              href="#what"
              data-testid="landing-learnmore"
              className="text-sm text-[#94A3B8] hover:text-white transition-colors"
            >
              How it works →
            </a>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, delay: 0.2 }}
          className="relative aspect-square max-w-md mx-auto"
        >
          {/* Glowing orb */}
          <div className="absolute inset-0 rounded-full"
               style={{
                 background:
                   "radial-gradient(circle at 50% 50%, rgba(212,175,55,0.25) 0%, rgba(212,175,55,0.05) 40%, transparent 70%)",
               }}
          />
          <div className="absolute inset-8 rounded-full border border-[#D4AF37]/30 animate-aura" style={{ color: "#D4AF37" }} />
          <div className="absolute inset-16 rounded-full border border-[#D4AF37]/20" />
          <div className="absolute inset-24 rounded-full border border-[#D4AF37]/10" />
          <div className="absolute inset-0 flex items-center justify-center">
            <Moon size={64} strokeWidth={1} className="text-[#D4AF37] animate-aura" style={{ color: "#D4AF37" }} />
          </div>
        </motion.div>
      </main>

      <section id="what" className="max-w-7xl mx-auto px-6 md:px-12 pb-24" data-testid="features">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { t: "Bodygraph", d: "Your Human Design type, strategy & authority — visualised with a living aura.", icon: Stars },
            { t: "Natal Chart", d: "Swiss Ephemeris planet positions across the twelve signs.", icon: Sparkles },
            { t: "Daily Quests", d: "Micro-rituals aligned with your specific chart. Earn XP. Grow.", icon: Moon },
          ].map((f, i) => (
            <motion.div
              key={f.t}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              viewport={{ once: true }}
              className="rounded-xl border border-white/10 bg-[#111625]/60 backdrop-blur-xl p-6"
            >
              <f.icon size={20} strokeWidth={1.5} className="text-[#D4AF37] mb-3" />
              <h3 className="font-serif-mystic text-2xl mb-1">{f.t}</h3>
              <p className="text-sm text-[#94A3B8]">{f.d}</p>
            </motion.div>
          ))}
        </div>
      </section>
    </CosmicBackground>
  );
}
